interface Options<T, U> {
  rule: 'filter' | 'hasPermissionAction' | 'hasNotPermissionAction'
  modifyItem?: (item: T) => U
}

/**
 * Обрабатывает массив элементов на основе правил, заданных в объекте `options`, и функции проверки разрешений.
 * Функция может фильтровать элементы или модифицировать их в зависимости от значения `options.rule`.
 *
 * @param {function(string): boolean} hasPermission - Функция проверки наличия разрешения.
 * Принимает строку (название разрешения) и возвращает `true`, если разрешение присутствует.
 *
 * @param {T[]} array - Массив элементов, которые необходимо обработать.
 *
 * @param {Options<T, U>} options - Объект с настройками обработки:
 * - `rule`: Определяет режим обработки. Может принимать значения:
 *   - `'filter'`: Фильтрует элементы, оставляя только те, у которых есть хотя бы одно разрешение.
 *   - `'hasPermissionAction'`: Модифицирует элементы, у которых есть хотя бы одно разрешение, с помощью `modifyItem`.
 *   - `'hasNotPermissionAction'`: Модифицирует элементы, у которых нет разрешений, с помощью `modifyItem`.
 * - `modifyItem` (опционально): Функция, которая применяется к элементам при выполнении `hasPermissionAction` или `hasNotPermissionAction`.
 *   Принимает элемент типа `T` и возвращает элемент типа `U`.
 *
 * @returns {(T | U)[]} - Возвращает обработанный массив, содержащий элементы типа `T` или `U` в зависимости от применённых действий.
 *
 * @description
 * Функция сначала фильтрует элементы массива, если `options.rule` установлен в `'filter'`.
 * В остальных случаях элементы остаются в массиве, но могут быть модифицированы с помощью `modifyItem`,
 * если оно определено и соответствует правилу `options.rule`.
 *
 * Если `modifyItem` не задан, элементы возвращаются без изменений.
 *
 * Важно: Для корректной работы функции элементы массива должны содержать свойство `permissions`,
 * которое является массивом строк (названий разрешений).
 */
export const processArray = <T, U>(
  hasPermission: (permission: string) => boolean,
  array: T[],
  options: Options<T, U> = { rule: 'filter' },
): (T | U)[] => {
  return array
    .filter(item => {
      const permissions = item['permissions'];
      if (!Array.isArray(permissions)) return true;

      if (options.rule === 'filter') {
        return permissions.some(perm => hasPermission(perm));
      }

      return true;
    })
    .map(item => {
      const permissions = item['permissions'];
      if (!Array.isArray(permissions)) return item;

      if (options.modifyItem) {
        const hasPerm = permissions.some(perm => hasPermission(perm))

        if (hasPerm && options.rule === 'hasPermissionAction') {
          return options.modifyItem(item)
        }

        if (!hasPerm && options.rule === 'hasNotPermissionAction') {
          return options.modifyItem(item)
        }
      }

      return item;
    });
}
